import type { IntlShape } from 'react-intl';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { OUTREACH_RAMP } from 'fym-shared/src/music24/outreach/mailboxRamp';
import { MAILBOX_GUIDE_COPY } from '../../../music24/outreach/mailboxGuide/mailboxGuideCopy';
import { isGoogleWorkspace } from '../../../music24/outreach/mailboxGuide/mailboxGuideState';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { mailboxMessages as messages } from './mailboxMessages';

/** The facts of the provider line. `providerId` may miss in the answer of an older API. */
type ProviderFacts = { providerId?: MailboxProviderId | null; address: string };

/**
 * The name of the mail service of a row (design outreach-v2 q1): "Google Workspace" for Gmail on an
 * own domain, "Your web host" for a host that we do not know, else the name of the provider. An
 * older API sends no `providerId`: then the row says "Your web host".
 */
export function mailboxProviderName(intl: IntlShape, mailbox: ProviderFacts): string {
  const id = mailbox.providerId ?? 'other';
  if (id === 'other') {
    return intl.formatMessage(messages.webHost);
  }
  if (isGoogleWorkspace(id, mailbox.address)) {
    return intl.formatMessage(messages.googleWorkspace);
  }
  return intl.formatMessage(MAILBOX_GUIDE_COPY[id].label);
}

/**
 * "Your web host · added by you on Oct 27" (design outreach-v2 q1, q3). A mailbox whose user is
 * gone, or a demo row, says only when it was connected.
 */
export function addedByLine(
  intl: IntlShape,
  mailbox: Pick<MailboxRow, 'createdBy' | 'connectedAt'> & ProviderFacts
): string {
  const provider = mailboxProviderName(intl, mailbox);
  const date = intl.formatDate(new Date(mailbox.connectedAt), { day: 'numeric', month: 'short' });
  const by = mailbox.createdBy;
  if (by === null) {
    return intl.formatMessage(messages.connectedOn, { provider, date });
  }
  if (by.isYou) {
    return intl.formatMessage(messages.addedByYou, { provider, date });
  }
  return intl.formatMessage(by.isOwner ? messages.addedByOwner : messages.addedByMember, {
    provider,
    name: by.name,
    date,
  });
}

/** The cap of each warm-up day 0 to `lastDay` from fym-shared `OUTREACH_RAMP` (one bar per day). */
export function rampCapsByDay(lastDay: number): number[] {
  return Array.from({ length: lastDay + 1 }, (_, day) =>
    OUTREACH_RAMP.reduce((cap, step) => (day >= step.fromDay ? step.perDay : cap), OUTREACH_RAMP[0]?.perDay ?? 0)
  );
}

/** The first and the last cap of the ramp (3 and 50 today). */
export function rampEnds(): { first: number; max: number } {
  return { first: OUTREACH_RAMP[0]?.perDay ?? 0, max: OUTREACH_RAMP[OUTREACH_RAMP.length - 1]?.perDay ?? 0 };
}
